import { Logger } from '../core/Logger';
import type { InitResponse } from '../orchestration/InstructionExecutor';
import { ONSITE_POLICY } from '@hamla/shared/onsite';

/**
 * CampaignFetcher — Fetches render instructions from /api/sdk/init
 *
 * Server-decided orchestration: the server returns pre-computed
 * trigger-indexed instructions, not raw campaigns.
 */
export class CampaignFetcher {
  private logger: Logger;
  private baseURL: string;

  constructor(baseURL: string = 'http://localhost:3000') {
    this.logger = new Logger('CampaignFetcher');
    this.baseURL = baseURL;
  }

  /**
   * Fetch render instructions from /api/sdk/init
   */
  async fetchInstructions(params: {
    businessId?: string;
    platformId?: string | number;
    platform?: string;
    visitorId?: string;
    sessionId?: string;
    pageContext?: {
      url: string;
      path: string;
      pageType: string;
      productId?: string;
      collectionId?: string;
      collectionName?: string;
      searchQuery?: string;
    };
    cart?: {
      total: number;
      itemCount: number;
      lastItemName: string | null;
      currency: string;
    };
    isFirstVisit?: boolean;
    referrer?: string;
    /** 'web' | 'ios_app' | 'android_app' — see `../app-surface`. */
    appSurface?: string;
    /** 'marketing' | 'product', as declared on the snippet's `data-env`. */
    environment?: string;
    utmParams?: {
      source?: string;
      medium?: string;
      campaign?: string;
      content?: string;
      term?: string;
    } | null;
  }): Promise<{ success: true; data: InitResponse } | { success: false; error: string }> {
    const url = `${this.baseURL}/api/sdk/init`;

    const requestBody: Record<string, unknown> = {
      preloadVariables: true,
    };

    if (params.businessId) requestBody.businessId = params.businessId;
    if (params.platformId) requestBody.platformId = params.platformId;
    if (params.platform) requestBody.platform = params.platform;
    if (params.sessionId) requestBody.sessionId = params.sessionId;
    if (params.visitorId) requestBody.visitorId = params.visitorId;
    if (params.pageContext) requestBody.pageContext = params.pageContext;
    if (params.cart) requestBody.cart = params.cart;
    if (params.isFirstVisit !== undefined) requestBody.isFirstVisit = params.isFirstVisit;
    if (params.appSurface) requestBody.appSurface = params.appSurface;
    if (params.environment) requestBody.environment = params.environment;
    if (params.referrer) requestBody.referrer = params.referrer;
    if (params.utmParams) requestBody.utmParams = params.utmParams;

    try {
      const response = await Promise.race([
        fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        }),
        new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Request timeout')), 10000)
        ),
      ]);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const json = await response.json();

      if (!json.success || !json.data?.instructions) {
        throw new Error(json.error || 'Invalid response');
      }

      const data: InitResponse = {
        instructions: json.data.instructions,
        storeInfo: json.data.storeInfo,
        variables: json.data.variables,
        sessionLimits: json.data.sessionLimits || { maxOnSiteMessages: ONSITE_POLICY.maxOnSiteMessagesPerSession },
        webPushOptIn: json.data.webPushOptIn,
      };

      this.logger.info('Instructions fetched', {
        triggers: Object.keys(data.instructions),
        count: Object.values(data.instructions).filter(v => v !== null).length,
      });

      return { success: true, data };
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error';
      this.logger.error('Fetch failed', { error: message });

      // One retry after 2s
      try {
        await new Promise(r => setTimeout(r, 2000));
        const retryResponse = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        });

        if (!retryResponse.ok) throw new Error('Retry failed');

        const retryJson = await retryResponse.json();
        if (!retryJson.success || !retryJson.data?.instructions) throw new Error('Retry invalid');

        const data: InitResponse = {
          instructions: retryJson.data.instructions,
          storeInfo: retryJson.data.storeInfo,
          variables: retryJson.data.variables,
          sessionLimits: retryJson.data.sessionLimits || { maxOnSiteMessages: ONSITE_POLICY.maxOnSiteMessagesPerSession },
          webPushOptIn: retryJson.data.webPushOptIn,
        };

        this.logger.info('Retry succeeded');
        return { success: true, data };
      } catch {
        this.logger.error('Retry also failed');
        return { success: false, error: message };
      }
    }
  }
}
